import { useState } from "react";
import { useHomepageVideos } from "@/hooks/useHomepageVideos";
import { updateHomepageVideo } from "@/lib/api";

import { AdminPanelHeader } from "./AdminPanelHeader";
import { FileUpload } from "./FileUpload";

type HomepageVideoEdit = {
  poster_url: string;
  video_url: string;
  mobile_video_url: string;
};

type HomepageVideoOriginal = {
  poster_url?: string | null;
  video_url: string;
  mobile_video_url?: string | null;
};

export function VideoSlotEditor() {
  const { data: videos, loading, refetch } = useHomepageVideos();
  const [savingSlot, setSavingSlot] = useState<number | null>(null);
  const [edits, setEdits] = useState<Record<number, HomepageVideoEdit>>({});

  const getEdit = (slot: number, original: HomepageVideoOriginal) =>
    edits[slot] ?? {
      poster_url: original.poster_url ?? "",
      video_url: original.video_url,
      mobile_video_url: original.mobile_video_url ?? "",
    };

  const handleChange = (
    slot: number,
    field: keyof HomepageVideoEdit,
    value: string,
    original: HomepageVideoOriginal,
  ) => {
    const current = getEdit(slot, original);
    setEdits((prev) => ({
      ...prev,
      [slot]: { ...current, [field]: value },
    }));
  };

  const handleSave = async (slot: number, original: HomepageVideoOriginal) => {
    const edit = getEdit(slot, original);
    setSavingSlot(slot);
    await updateHomepageVideo(slot, {
      poster_url: edit.poster_url || undefined,
      video_url: edit.video_url,
      mobile_video_url: edit.mobile_video_url || null,
    });
    setSavingSlot(null);
    setEdits((prev) => {
      const next = { ...prev };
      delete next[slot];
      return next;
    });
    refetch();
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-pink-dark border-t-transparent" />
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <AdminPanelHeader title="Homepage Videos" />
        <p className="mt-2 text-sm text-gray-500">
          Max video upload size: 250 MB per file. Use a compressed 360p/480p mobile preview for faster homepage loading.
        </p>
      </div>
      <div className="grid gap-4 sm:grid-cols-3">
        {(videos ?? []).map((video) => {
          const edit = getEdit(video.slot, video);
          return (
            <div
              key={video.slot}
              className="rounded-xl border border-gray-200 bg-white p-4"
            >
              <div className="mb-3 text-sm font-semibold text-gray-500">
                Slot {video.slot}
              </div>

              {(edit.poster_url || video.poster_url) && (
                <div className="mb-3 aspect-video overflow-hidden rounded-lg bg-gray-100">
                  <img
                    src={edit.poster_url || video.poster_url || ""}
                    alt={`Slot ${video.slot} poster`}
                    className="h-full w-full object-cover"
                  />
                </div>
              )}

              <div className="mb-3">
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Poster Image
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={edit.poster_url}
                    onChange={(e) =>
                      handleChange(video.slot, "poster_url", e.target.value, video)
                    }
                    className="min-w-0 flex-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
                    placeholder="https://..."
                  />
                  <FileUpload
                    folder="videos/posters"
                    accept="image/*"
                    label="Upload"
                    onUploaded={(url) => handleChange(video.slot, "poster_url", url, video)}
                  />
                </div>
              </div>

              <div className="mb-3">
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Video
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={edit.video_url}
                    onChange={(e) =>
                      handleChange(video.slot, "video_url", e.target.value, video)
                    }
                    className="min-w-0 flex-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
                    placeholder="https://..."
                  />
                  <FileUpload
                    folder="videos"
                    accept="video/mp4,video/webm"
                    label="Upload"
                    onUploaded={(url) => handleChange(video.slot, "video_url", url, video)}
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Mobile Preview Video (optional)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={edit.mobile_video_url}
                    onChange={(e) =>
                      handleChange(video.slot, "mobile_video_url", e.target.value, video)
                    }
                    className="min-w-0 flex-1 rounded-lg border border-gray-300 px-2.5 py-1.5 text-sm focus:border-pink-dark focus:outline-none focus:ring-1 focus:ring-pink-dark"
                    placeholder="https://..."
                  />
                  <FileUpload
                    folder="videos/mobile"
                    accept="video/mp4,video/webm"
                    label="Upload"
                    onUploaded={(url) => handleChange(video.slot, "mobile_video_url", url, video)}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-400">Loaded first on mobile; falls back to the main video when empty.</p>
              </div>

              <button
                type="button"
                onClick={() => handleSave(video.slot, video)}
                disabled={savingSlot === video.slot || !edit.video_url.trim()}
                className="w-full rounded-xl bg-pink-dark px-4 py-2 text-sm font-semibold text-white transition-transform hover:-translate-y-px active:scale-95 disabled:opacity-50"
              >
                {savingSlot === video.slot ? "Saving…" : "Save"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
